/** Chrome every step shares: a heading, a one-line explanation, and the inline save error.
 *
 *  The error lives on the step rather than in a snackbar on purpose — the coach has to fix
 *  something *here*, and `MessageContext`'s toast auto-hides after 3s and would be gone by the
 *  time they look back at the form. */

import type { ReactNode } from "react";
import { Box } from "@mui/material";
import { ErrorOutline as ErrorIcon } from "@mui/icons-material";
import { t } from "../../dashboard/coach/coachTokens";

/** A two-column grid on desktop, one column on a phone — the shape most steps want. */
export const StepGrid = ({ children }: { children: ReactNode }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
      gap: "16px",
      alignItems: "start",
    }}
  >
    {children}
  </Box>
);

interface StepShellProps {
  title: string;
  description?: string;
  error?: string | null;
  children: ReactNode;
}

const StepShell = ({ title, description, error, children }: StepShellProps) => (
  <Box>
    {/* A real heading, not a styled div: it is the page's "where am I", and it keeps the title
        distinguishable from the same label in the step rail. */}
    <Box component="h2" sx={{ margin: 0, fontSize: "18px", fontWeight: 700, color: t.text }}>
      {title}
    </Box>
    {description && (
      <Box sx={{ fontSize: "13px", color: t.t2, marginTop: "4px", lineHeight: 1.5 }}>
        {description}
      </Box>
    )}

    {error && (
      <Box
        role="alert"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginTop: "16px",
          padding: "10px 12px",
          borderRadius: t.rs,
          backgroundColor: t.redLight,
          border: `1px solid ${t.red}33`,
          color: t.redText,
          fontSize: "13px",
          fontWeight: 500,
        }}
      >
        <ErrorIcon sx={{ fontSize: "18px" }} />
        {error}
      </Box>
    )}

    <Box sx={{ marginTop: "20px" }}>{children}</Box>
  </Box>
);

export default StepShell;
